/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

let csrf: string | null = null;

export function setCSRF(token: string) {
    csrf = token;
}

export function getCSRF(): string | null {
    return csrf;
}

const appLocalStorage: Record<string, any> = {};

export const saveToLocal = (key: string, value: string) => {
    try {
        window.localStorage?.setItem(key, value);
    } catch {
        // storage unavailable (sandboxed iframe, blocked site data, private mode quota) — ignore
    }
};

export const getFromLocal = (key: string): string | null => {
    try {
        return window.localStorage?.getItem(key) ?? null;
    } catch {
        return null;
    }
};

export const deleteFromLocal = (key: string) => {
    try {
        window.localStorage?.removeItem(key);
    } catch {
        // storage unavailable — ignore
    }
};

export const saveToSession = (key: string, value: string) => {
    try {
        window.sessionStorage?.setItem(key, value);
    } catch {
        // storage unavailable — ignore
    }
};

export const getFromSession = (key: string): string | null => {
    try {
        return window.sessionStorage?.getItem(key) ?? null;
    } catch {
        return null;
    }
};

export const deleteFromSession = (key: string) => {
    try {
        window.sessionStorage?.removeItem(key);
    } catch {
        // storage unavailable — ignore
    }
};

export const saveToAppLocal = (key: string, value: any) => {
    appLocalStorage[key] = value;
};

export const getFromAppLocal = (key: string): any => {
    return appLocalStorage[key];
};

export const deleteFromAppLocal = (key: string) => {
    delete appLocalStorage[key];
};

export function logAppLocal() {
    return appLocalStorage;
}

export const IGNORE_BOL_ISSUE_KEY = 'ignore_bol_issue';
export const CONFETTI_SHOWN_KEY = 'confetti_shown';
// Login email prefilled from a push-notification click (?push_email=…). Lives in
// sessionStorage so it survives a reload (e.g. connectionRecovery re-passing a
// Cloudflare challenge); cleared on successful login AND on logout (it is PII).
export const PUSH_EMAIL_KEY = 'bluemate-push-login-email';
// Per-login banner dismissals (InfoBanner `sessionDismiss`). They live in
// sessionStorage so they survive a reload, but every flow that establishes a new
// login (password/Google/2FA login, registration, admin impersonation) clears the
// whole prefix so dismissed banners return on each new login.
export const SESSION_BANNER_CLOSED_PREFIX = 'bluemate-banner-closed-';

export const bannerSessionKey = (featureKey: string) => `${SESSION_BANNER_CLOSED_PREFIX}${featureKey}`;

export const clearSessionBannerDismissals = () => {
    try {
        const storage = window.sessionStorage;
        if (!storage) return;
        for (let index = storage.length - 1; index >= 0; index--) {
            const key = storage.key(index);
            if (key?.startsWith(SESSION_BANNER_CLOSED_PREFIX)) storage.removeItem(key);
        }
    } catch {
        // storage unavailable — ignore
    }
};
